import { type ComponentProps, type ReactNode, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Icon } from '../../shared/Icon';
import { ElementTile } from './ElementTile';

import type { JiraSubtask } from '../../hooks/useJiraApi';

import { formatDateRange } from '../utils/dates';

type ElementTileProps = ComponentProps<typeof ElementTile>;

export type PlannedGiftTileStatus =
    | 'planned'
    | 'ready'
    | 'incomplete'
    | 'scheduled';

export interface PlannedGiftTileProps {
    subtask: JiraSubtask;
    status: PlannedGiftTileStatus;
    onSelect?: (subtask: JiraSubtask) => void;
    size?: ElementTileProps['size'];
}

/**
 * Status-aware presentation for a Jira gift plan. This owns planned tile image
 * selection, status treatment, tooltip, and accessible label so planner and
 * calendar render an identical representation.
 */
export const PlannedGiftTile = ({
    subtask,
    status,
    onSelect,
    size,
}: PlannedGiftTileProps) => {
    const details = statusDetails[status];
    const dateRange = formatDateRange(subtask.startTime, subtask.endTime);
    const isPlanned = status === 'planned' || status === 'ready';

    const gameElementIds = useMemo(() => {
        const ids = [];
        if (subtask.contentGameElementId) {
            ids.push(subtask.contentGameElementId);
        }
        if (subtask.wrappingGameElementId) {
            ids.push(subtask.wrappingGameElementId);
        }
        return ids;
    }, [subtask]);

    return (
        <ElementTile
            gameElementIds={gameElementIds}
            fallbackImage={subtask.thumbnail}
            size={size}
            ariaLabel={`Planned gift ${subtask.summary} (${subtask.key}): ${details.label}`}
            onClick={() => onSelect?.(subtask)}
            sx={
                status === 'scheduled'
                    ? { borderColor: 'success.main', borderWidth: 2 }
                    : isPlanned
                      ? plannedTileSx
                      : undefined
            }
            bigSx={isPlanned ? plannedTileBigSx : undefined}
            badge={details.badge}
            badgePosition="top-right"
            tooltip={
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {subtask.summary}
                    </Typography>
                    {dateRange && (
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', opacity: 0.85 }}
                        >
                            {dateRange}
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            mt: 0.5,
                            fontWeight: 600,
                            color: details.color,
                        }}
                    >
                        {details.label}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', opacity: 0.7 }}
                    >
                        {subtask.key} · click to view
                    </Typography>
                </Box>
            }
        />
    );
};

const plannedTileSx: NonNullable<ElementTileProps['sx']> = {
    border: 'none',
    outline: '2px dashed',
    outlineColor: 'warning.main',
    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
};

const plannedTileBigSx: NonNullable<ElementTileProps['bigSx']> = {
    bgcolor: 'transparent',
    opacity: 0.85,
};

const statusDetails: Record<
    PlannedGiftTileStatus,
    { label: string; color: string; badge?: ReactNode }
> = {
    planned: {
        label: 'Planned - not yet scheduled',
        color: 'warning.light',
        badge: <StatusBadge color="warning.main" icon="schedule" />,
    },
    ready: {
        label: 'Ready to schedule',
        color: 'warning.light',
        badge: <StatusBadge color="warning.main" icon="schedule" />,
    },
    incomplete: { label: 'Plan incomplete', color: 'text.disabled' },
    scheduled: {
        label: 'Scheduled',
        color: 'success.light',
        badge: <StatusBadge color="success.main" icon="check_circle" />,
    },
};

function StatusBadge({
    color,
    icon,
}: {
    color: string;
    icon: ComponentProps<typeof Icon>['name'];
}) {
    return (
        <Box
            sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: color,
                color: 'common.white',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: '1px solid',
                outlineColor: 'background.paper',
            }}
        >
            <Icon name={icon} />
        </Box>
    );
}
